import useMouseEvents from '@/roadmaps/use/use-mouse-events';
import { computed, reactive, ref, unref } from 'vue';

const isDragging = ref(false);
const draggedItem = ref(null);
const draggedItemPosition = reactive({
    left: null,
    right: null,
    top: null,
});
const draggedItemCursorOffset = reactive({
    left: null,
    right: null,
    top: null,
});

export function useDraggable () {
    let draggedElement;
    let initialCursorXY;
    let onBeforeDragStart = () => {};
    let onDragStart = () => {};
    let onDragging = () => {};
    let onDragStop = () => {};
    let onAfterDragStop = () => {};

    const registerEvents = (events) => {
        if (events.onBeforeDragStart) onBeforeDragStart = events.onBeforeDragStart;
        if (events.onDragStart) onDragStart = events.onDragStart;
        if (events.onDragging) onDragging = events.onDragging;
        if (events.onDragStop) onDragStop = events.onDragStop;
        if (events.onAfterDragStop) onAfterDragStop = events.onAfterDragStop;
    };

    const setDraggedItem = (item) => {
        draggedItem.value = item;
    };

    const setDraggedItemCursorOffset = ({ left, right, top }) => {
        draggedItemCursorOffset.left = left;
        draggedItemCursorOffset.right = right;
        draggedItemCursorOffset.top = top;
    };

    const getIsItemBeingDraggedById = (itemId) => {
        if (!unref(draggedItem)) return false;

        return unref(draggedItem).id === itemId;
    };

    const isSomethingBeingDragged = computed(() => {
        return !!unref(draggedItem);
    });

    const isDraggingWithTolerance = ({ pageX, pageY }) => {
        const xDiff = Math.abs(initialCursorXY.x - pageX);
        const yDiff = Math.abs(initialCursorXY.y - pageY);

        return xDiff > 2 || yDiff > 2;
    };

    const onMouseMove = (event) => {
        event.preventDefault();
        if (!unref(isDragging) && isDraggingWithTolerance({ pageX: event.pageX, pageY: event.pageY })) {
            const xDiff = Math.abs(initialCursorXY.x - event.pageX);
            const yDiff = Math.abs(initialCursorXY.y - event.pageY);

            if (xDiff > 2 || yDiff > 2) {
                isDragging.value = true;
                onDragStart({
                    elem: draggedElement,
                    pageX: Math.floor(event.pageX),
                    pageY: Math.floor(event.pageY),
                });
            }
        }
        if (unref(isDragging)) {
            draggedItemPosition.left = event.pageX + draggedItemCursorOffset.left;
            draggedItemPosition.right = event.pageX + draggedItemCursorOffset.right;
            draggedItemPosition.top = event.pageY + draggedItemCursorOffset.top;

            onDragging({
                pageX: Math.floor(event.pageX),
                pageY: Math.floor(event.pageY),
            });
        }
    };

    const onPointerUp = (event) => {
        event.preventDefault();

        const { removeMouseMoveHandler } = useMouseEvents();
        removeMouseMoveHandler(onMouseMove);

        window.removeEventListener('pointerup', onPointerUp);
        document.body.style.cursor = 'initial';

        if (unref(isDragging)) {
            onDragStop({
                elem: draggedElement,
            });
            isDragging.value = false;
        }
        onAfterDragStop();
    };

    const onDrag = (event) => {
        if (event.button !== 0) return;
        initialCursorXY = {
            x: event.pageX,
            y: event.pageY,
        };
        event.stopPropagation();
        event.preventDefault();

        draggedElement = event.currentTarget;

        onBeforeDragStart({
            elem: draggedElement,
            pageX: Math.floor(event.pageX),
            pageY: Math.floor(event.pageY),
        });

        const { addMouseMoveHandler } = useMouseEvents();
        addMouseMoveHandler(onMouseMove);

        window.addEventListener('pointerup', onPointerUp);
        document.body.style.cursor = 'grabbing';
    };

    return {
        isActivelyDragging: isDragging,
        draggedItem,
        draggedItemPosition,
        draggedItemCursorOffset,
        isSomethingBeingDragged,

        registerEvents,
        onDrag,
        setDraggedItem,
        setDraggedItemCursorOffset,
        getIsItemBeingDraggedById,
    };
}
